import {useState, useEffect, useCallback} from "react"
import {
    Box, Typography, Paper, Grid, FormControl, InputLabel, Select,
    MenuItem, CircularProgress, Card, CardContent, Alert,
} from "@mui/material"
import {
    TrendingUp as TrendingUpIcon, ShoppingCart as ShoppingCartIcon,
    People as PeopleIcon, AttachMoney as AttachMoneyIcon,
} from "@mui/icons-material"
import {useTranslation} from "react-i18next"
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip as RechartsTooltip, ResponsiveContainer,
    LineChart, Line, PieChart, Pie, Cell, Legend,
} from "recharts"
import axiosInstance from "../../config/axios-config"
import i18n from "i18next"

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884d8", "#82ca9d"]

const SummaryCard = ({icon, title, value, color, trend}) => (
    <Card elevation={1} sx={{height: "100%"}}>
        <CardContent>
            <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "flex-start"}}>
                <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>{title}</Typography>
                    <Typography variant="h4" fontWeight={700}>{value}</Typography>
                    {trend && (
                        <Box sx={{display: "flex", alignItems: "center", mt: 1}}>
                            <TrendingUpIcon sx={{fontSize: 18, color: "success.main", mr: 0.5}}/>
                            <Typography variant="body2" color="success.main">{trend}</Typography>
                        </Box>
                    )}
                </Box>
                <Box sx={{
                    bgcolor: `${color}20`, borderRadius: 2, p: 1.5,
                    display: "flex", alignItems: "center", justifyContent: "center"
                }}>
                    {icon}
                </Box>
            </Box>
        </CardContent>
    </Card>
)

export default function TabAdminAnalytics({tabValue}) {
    const {t} = useTranslation()
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [period, setPeriod] = useState("MONTHLY")
    const [revenueData, setRevenueData] = useState([])
    const [ordersData, setOrdersData] = useState([])
    const [categoryData, setCategoryData] = useState([])
    const [summaryStats, setSummaryStats] = useState({
        totalRevenue: 0, totalOrders: 0, newUsers: 0, avgOrderValue: 0,
    })

    const fetchAnalytics = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const res = await axiosInstance.get("/api/v1/statics/admin-analytics", {
                params: {period},
                headers: {"Language-Context": (i18n.language || "en").toUpperCase()},
            })
            const data = res.data
            setRevenueData(data.revenueData || [])
            setOrdersData(data.ordersData || [])
            setCategoryData(data.categoryData || [])
            setSummaryStats(data.summary || {totalRevenue: 0, totalOrders: 0, newUsers: 0, avgOrderValue: 0})
        } catch (err) {
            setError(err.response?.data?.message || t("FailedToLoadAnalytics"))
        } finally {
            setLoading(false)
        }
    }, [period])

    useEffect(() => {
        if (tabValue === 3) fetchAnalytics()
    }, [tabValue, fetchAnalytics, i18n.language])

    if (tabValue !== 3) return null

    return (
        <Box>
            {/* Period Filter */}
            <Box sx={{display: "flex", justifyContent: "flex-end", mb: 3}}>
                <FormControl size="small" sx={{minWidth: 160}}>
                    <InputLabel>{t("Period") || "Period"}</InputLabel>
                    <Select value={period} onChange={(e) => setPeriod(e.target.value)} label={t("Period") || "Period"}>
                        <MenuItem value="DAILY">{t("Daily") || "Daily"}</MenuItem>
                        <MenuItem value="WEEKLY">{t("Weekly") || "Weekly"}</MenuItem>
                        <MenuItem value="MONTHLY">{t("Monthly") || "Monthly"}</MenuItem>
                        <MenuItem value="YEARLY">{t("Yearly") || "Yearly"}</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            {error && <Alert severity="error" sx={{mb: 3}}>{error}</Alert>}

            {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                    <CircularProgress/>
                </Box>
            ) : (
                <>
                    {/* Summary Cards */}
                    <Grid container spacing={3} sx={{mb: 4}}>
                        <Grid item xs={12} sm={6} md={3}>
                            <SummaryCard
                                icon={<AttachMoneyIcon sx={{color: "#4caf50", fontSize: 28}}/>}
                                title={t("TotalRevenue") || "Total Revenue"}
                                value={`${summaryStats.totalRevenue?.toLocaleString() || 0}֏`}
                                color="#4caf50" trend="+12.5%"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <SummaryCard
                                icon={<ShoppingCartIcon sx={{color: "#2196f3", fontSize: 28}}/>}
                                title={t("TotalOrders") || "Total Orders"}
                                value={summaryStats.totalOrders?.toLocaleString() || 0}
                                color="#2196f3" trend="+8.2%"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <SummaryCard
                                icon={<PeopleIcon sx={{color: "#ff9800", fontSize: 28}}/>}
                                title={t("NewUsers") || "New Users"}
                                value={summaryStats.newUsers?.toLocaleString() || 0}
                                color="#ff9800" trend="+5.1%"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <SummaryCard
                                icon={<TrendingUpIcon sx={{color: "#9c27b0", fontSize: 28}}/>}
                                title={t("AvgOrderValue") || "Avg Order Value"}
                                value={`${summaryStats.avgOrderValue?.toLocaleString() || 0}֏`}
                                color="#9c27b0" trend="+3.7%"
                            />
                        </Grid>
                    </Grid>

                    {/* Revenue + Category */}
                    <Grid container spacing={3} sx={{mb: 4}}>
                        <Grid item xs={12} md={8}>
                            <Paper elevation={1} sx={{p: 3}}>
                                <Typography variant="h6" gutterBottom>
                                    {t("RevenueOverview") || "Revenue Overview"}
                                </Typography>
                                {!revenueData.length ? (
                                    <Box display="flex" justifyContent="center" alignItems="center" height={320}>
                                        <Typography color="textSecondary">{t("NoDataAvailable")}</Typography>
                                    </Box>
                                ) : (
                                    <ResponsiveContainer width="100%" height={320}>
                                        <BarChart data={revenueData}>
                                            <CartesianGrid strokeDasharray="3 3"/>
                                            <XAxis dataKey="name"/>
                                            <YAxis/>
                                            <RechartsTooltip
                                                formatter={(v) => [`${v.toLocaleString()}֏`, t("Revenue")]}/>
                                            <Bar dataKey="revenue" fill="#0088FE" radius={[4, 4, 0, 0]}
                                                 name={t("Revenue")}/>
                                        </BarChart>
                                    </ResponsiveContainer>
                                )}
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Paper elevation={1} sx={{p: 3}}>
                                <Typography variant="h6" gutterBottom>
                                    {t("SalesByCategory") || "Sales by Category"}
                                </Typography>
                                {!categoryData.length ? (
                                    <Box display="flex" justifyContent="center" alignItems="center" height={320}>
                                        <Typography color="textSecondary">{t("NoDataAvailable")}</Typography>
                                    </Box>
                                ) : (
                                    <ResponsiveContainer width="100%" height={320}>
                                        <PieChart>
                                            <Pie data={categoryData} cx="50%" cy="50%"
                                                 outerRadius={100} dataKey="value"
                                                 label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                            >
                                                {categoryData.map((_, idx) => (
                                                    <Cell key={idx} fill={COLORS[idx % COLORS.length]}/>
                                                ))}
                                            </Pie>
                                            <Legend/>
                                            <RechartsTooltip/>
                                        </PieChart>
                                    </ResponsiveContainer>
                                )}
                            </Paper>
                        </Grid>
                    </Grid>

                    {/* Orders Trend */}
                    <Paper elevation={1} sx={{p: 3}}>
                        <Typography variant="h6" gutterBottom>
                            {t("OrdersTrend") || "Orders Trend"}
                        </Typography>
                        {!ordersData.length ? (
                            <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                                <Typography color="textSecondary">{t("NoDataAvailable")}</Typography>
                            </Box>
                        ) : (
                            <ResponsiveContainer width="100%" height={300}>
                                <LineChart data={ordersData}>
                                    <CartesianGrid strokeDasharray="3 3"/>
                                    <XAxis dataKey="name"/>
                                    <YAxis/>
                                    <RechartsTooltip/>
                                    <Line type="monotone" dataKey="orders" stroke="#00C49F" strokeWidth={2} dot={{r: 4}}
                                          name={t("Orders")}/>
                                    <Line type="monotone" dataKey="cancelled" stroke="#FF8042" strokeWidth={2}
                                          dot={{r: 4}} name={t("Cancelled")}/>
                                    <Legend/>
                                </LineChart>
                            </ResponsiveContainer>
                        )}
                    </Paper>
                </>
            )}
        </Box>
    )
}
